import React, { useRef, useState } from "react";
import {
  X,
  Upload,
  FileText,
  ChevronDown,
  Image as ImageIcon,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { uploadCustomerDocument } from "../../store/slices/epcSlice";
import toast from "react-hot-toast";
import Button, { ButtonType } from "../Assets/Button";

interface AddEpcDocsModalProps {
  isOpen: boolean;
  onClose: (success?: boolean) => void;
  customerName: string;
  customerId?: string | null;
  onUploaded?: () => void;
}

type DocumentType =
  | "feasibility"
  | "eToken"
  | "aadhaarFront"
  | "aadhaarBack"
  | "lightBill"
  | "bankDetails"
  | "passportPhoto"
  | "custom";
interface DocumentTypeInfo {
  value: DocumentType;
  label: string;
  allowedFormats: string[];
  maxSize: number;
  formatLabel: string;
}

const AddEpcDocsModal: React.FC<AddEpcDocsModalProps> = ({
  isOpen,
  onClose,
  customerName,
  customerId,
  onUploaded,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const isUploadingDocument = useSelector(
    (state: RootState) => state.epc.loadingState.uploadingDocumentLoading,
  );

  const [selectedDocType, setSelectedDocType] =
    useState<DocumentType>("feasibility");
  const [customDocName, setCustomDocName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentTypes: DocumentTypeInfo[] = [
    {
      value: "feasibility",
      label: "Feasibility Letter",
      allowedFormats: [".pdf", "application/pdf"],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "PDF",
    },
    {
      value: "eToken",
      label: "E-Token",
      allowedFormats: [".pdf", "application/pdf"],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "PDF",
    },
    {
      value: "aadhaarFront",
      label: "Aadhaar Card (Front Side)",
      allowedFormats: [
        ".jpg",
        ".jpeg",
        ".png",
        ".pdf",
        "image/jpeg",
        "image/png",
        "application/pdf",
      ],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "Image or PDF",
    },
    {
      value: "aadhaarBack",
      label: "Aadhaar Card (Back Side)",
      allowedFormats: [
        ".jpg",
        ".jpeg",
        ".png",
        ".pdf",
        "image/jpeg",
        "image/png",
        "application/pdf",
      ],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "Image or PDF",
    },
    {
      value: "lightBill",
      label: "Light Bill",
      allowedFormats: [
        ".jpg",
        ".jpeg",
        ".png",
        ".pdf",
        "image/jpeg",
        "image/png",
        "application/pdf",
      ],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "Image or PDF",
    },
    {
      value: "bankDetails",
      label: "Bank Details",
      allowedFormats: [
        ".jpg",
        ".jpeg",
        ".png",
        ".pdf",
        "image/jpeg",
        "image/png",
        "application/pdf",
      ],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "Image or PDF",
    },
    {
      value: "passportPhoto",
      label: "Passport-size Photo",
      allowedFormats: [".jpg", ".jpeg", ".png", "image/jpeg", "image/png"],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "Image or PDF",
    },
    {
      value: "custom",
      label: "Custom Document",
      allowedFormats: [
        ".jpg",
        ".jpeg",
        ".png",
        ".pdf",
        "image/jpeg",
        "image/png",
        "application/pdf",
      ],
      maxSize: 5 * 1024 * 1024,
      formatLabel: "Image or PDF",
    },
  ];

  const selectedDocument = documentTypes.find(
    (doc) => doc.value === selectedDocType,
  );

  const validateFile = (file: File): string => {
    // Check file type
    const isTypeValid = selectedDocument?.allowedFormats.some(
      (format) =>
        file.type.includes(format.replace(".", "")) ||
        file.name.toLowerCase().endsWith(format),
    );

    if (!isTypeValid) {
      const allowedTypes = selectedDocument?.formatLabel || "PDF or Image";
      return `Only ${allowedTypes} files are allowed`;
    }

    // Check file size
    if (selectedDocument && file.size > selectedDocument.maxSize) {
      const maxSizeInMB = selectedDocument.maxSize / (1024 * 1024);
      return `File size must be less than ${maxSizeInMB}MB`;
    }

    return "";
  };

  const getAcceptAttribute = (): string => {
    if (!selectedDocument) return ".pdf";

    // Extract file extensions for accept attribute
    const extensions = selectedDocument.allowedFormats
      .filter((format) => format.startsWith("."))
      .join(",");

    const mimeTypes = selectedDocument.allowedFormats
      .filter((format) => !format.startsWith("."))
      .join(",");

    return extensions + (mimeTypes ? "," + mimeTypes : "");
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon className="w-4 h-4" />;
    }
    return <FileText className="w-4 h-4" />;
  };

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setError("");
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFile(null);
    setError("");
  };

  const handleUpload = async () => {
    if (!file || !customerId) {
      setError("Please select a file to upload");
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (selectedDocType === "custom" && !customDocName.trim()) {
      setError("Please enter document name");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const res = await dispatch(
        uploadCustomerDocument({
          customerId,
          documentName:
            selectedDocType === "custom"
              ? customDocName.trim()
              : selectedDocType,
          file,
        }),
      ).unwrap();
      if (res.success) {
        toast.success(`${selectedDocument?.label} uploaded successfully!`);
        clearFileInput();
      }

      if (onUploaded) {
        onUploaded();
      }
    } catch (error: any) {
      const errorMessage = error || "Failed to upload document";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    clearFileInput();
    setSelectedDocType("feasibility");
    setCustomDocName("");
    setError("");
    setIsUploading(false);
    setIsDropdownOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-xl bg-white rounded-lg shadow-xl overflow-hidden z-10 h-[60vh] flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Upload Document
            </h2>
            <p className="text-blue-100 text-sm">{customerName}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={isUploading || isUploadingDocument}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-all disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Document Type Selection */}
          <div className="space-y-2 mb-8">
            <label className="block text-sm font-semibold text-gray-700">
              Document Type
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg text-left transition-all ${
                  isDropdownOpen ? "ring-2 ring-opacity-50 " : ""
                }`}
                disabled={isUploading || isUploadingDocument}
              >
                <div className="flex items-center gap-3">
                  <div>
                    <span className="font-medium text-gray-900 block">
                      {selectedDocument?.label}
                    </span>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    isDropdownOpen ? "transform rotate-180" : ""
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <>
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {documentTypes.map((doc) => {
                      return (
                        <button
                          key={doc.value}
                          onClick={() => {
                            setSelectedDocType(doc.value);
                            setIsDropdownOpen(false);
                            clearFileInput();
                            setCustomDocName("");
                          }}
                          className={`w-full text-xs flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                            selectedDocType === doc.value ? "bg-gray-50" : ""
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <span className="font-medium text-gray-900 block">
                                {doc.label}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
          {selectedDocType === "custom" && (
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700">
                Document Name
              </label>
              <input
                type="text"
                value={customDocName}
                onChange={(e) => setCustomDocName(e.target.value)}
                placeholder="Enter document name"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                disabled={isUploading || isUploadingDocument}
              />
            </div>
          )}
          {/* File Upload Section */}
          <div className="border-2 rounded-xl p-4 transition-all">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                {selectedDocument?.label}
              </label>
              <span className="text-xs font-medium text-gray-500 text-right">
                {selectedDocument?.formatLabel} • Max{" "}
                {selectedDocument
                  ? selectedDocument.maxSize / (1024 * 1024)
                  : 5}
                MB
              </span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptAttribute()}
              onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              className="hidden"
              id="file-input"
              disabled={isUploading || isUploadingDocument}
            />

            <label
              htmlFor="file-input"
              className="flex items-center justify-center gap-2 w-full py-2.5 px-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer text-xs text-gray-700 bg-white hover:bg-gray-50 transition-all"
            >
              <Upload className="w-4 h-4" />
              {file
                ? "Change File"
                : `Choose ${selectedDocument?.formatLabel} File`}
            </label>

            {file && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2">
                <div>{getFileIcon(file.type)}</div>
                <div className="flex-1 min-w-0">
                  <span className="block truncate">{file.name}</span>
                  <span className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                  </span>
                </div>
                <button
                  onClick={clearFileInput}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isUploading || isUploadingDocument}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

            {file && (
              <button
                onClick={handleUpload}
                disabled={isUploading || isUploadingDocument}
                className="w-full mt-3 py-2.5 rounded-lg text-white bg-navyBlue-primary hover:bg-navyBlue-light font-medium text-sm flex items-center justify-center gap-2 transition-all shadow-sm"
              >
                {isUploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {selectedDocument?.label}
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <Button
            type={ButtonType.Cancel}
            onClick={handleClose}
            disabled={isUploading || isUploadingDocument}
            className="w-full text-sm"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddEpcDocsModal;
